import { Injectable, Inject } from '@angular/core';
import { UserCoreService } from './user-core.service';
import { ProjectService } from '../project/project.service';
import { User } from './user.interface';

/**
 * User service.
 *
 * The public interface for the UserCoreService, with shorthands for
 * each operation.
 */
@Injectable({
  providedIn: 'root'
})
export class UserService extends UserCoreService {

  /** Constants. */
  private readonly DEFAULT_USER_LANGUAGE = 'nl';

  constructor(
    @Inject('sgAuth') protected sgAuthService: any,
    protected projectService: ProjectService
  ) {
    super(sgAuthService, projectService);
  }

  /**
   * Get the (current) user's application role.
   *
   * @param [user] When defined, check for this user, otherwise check the current active user.
   * @returns The (current) user's application role.
   */
  public getAppRole(user?: User['_id']): User['role'] {
    if (user) {
      return this.get().find(u => u._id === user).role;
    }
    return this.getUserCurrent().role;
  }

  /**
   * Get the (current) user's email address.
   *
   * @param [user] When defined, check for this user, otherwise check the current active user.
   * @returns The (current) user's email address.
   */
  public getEmail(user?: User['_id']): User['email'] {
    if (user) {
      return this.get().find(u => u._id === user).email;
    }
    return this.getUserCurrent().email;
  }

  /**
   * Get the (current) user's first name.
   *
   * @param [user] When defined, check for this user, otherwise check the current active user.
   * @returns The (current) user's first name.
   */
  public getFirstName(user?: User['_id']): User['nameFirst'] {
    if (user) {
      return this.get().find(u => u._id === user).nameFirst;
    }
    return this.getUserCurrent().nameFirst;
  }

  /**
   * Get the (current) user's full name.
   *
   * @param [user] When defined, check for this user, otherwise check the current active user.
   * @returns The (current) user's full name.
   */
  public getFullName(user?: User['_id']): User['fullName'] {
    if (user) {
      return this.get().find(u => u._id === user).fullName;
    }
    return this.getUserCurrent().fullName;
  }

  /**
   * Get the (current) user's gravatar hash.
   *
   * @param [user] When defined, check for this user, otherwise check the current active user.
   * @returns The (current) user's gravatar hash.
   */
  public getGravatarHash(user?: User['_id']): User['gravatarHash'] {
    if (user) {
      return this.get().find(u => u._id === user).gravatarHash;
    }
    return this.getUserCurrent().gravatarHash;
  }

  /**
   * Get the (current) user'sid.
   *
   * @param [user] When defined, check for this user, otherwise check the current active user.
   * @returns The (current) user's id.
   */
  public getId(user?: User['_id']): User['_id'] {
    if (user) {
      return this.get().find(u => u._id === user)._id;
    }
    return this.getUserCurrent()._id;
  }

  /**
   * Get the (current) user's job description.
   *
   * @param [user] When defined, check for this user, otherwise check the current active user.
   * @returns The (current) user's job description.
   */
  public getJobDescription(user?: User['_id']): User['jobDescription'] {
    if (user) {
      return this.get().find(u => u._id === user).jobDescription;
    }
    return this.getUserCurrent().jobDescription;
  }

  /**
   * Get the (current) user's last activation email date.
   *
   * @param [user] When defined, check for this user, otherwise check the current active user.
   * @returns The (current) user's last activation email date.
   */
  public getLastActiviationEmail(user?: User['_id']): User['lastActivationMail'] {
    if (user) {
      return this.get().find(u => u._id === user).lastActivationMail;
    }
    return this.getUserCurrent().lastActivationMail;
  }

  /**
   * Get the (current) user's language.
   *
   * @note Fixed a workaround so this method can be accessed *outside* of projects, but
   *  there should probably be a better solution, without rewriting every method.
   *
   * @param [id] When defined, check for this user, otherwise check the current active user.
   * @returns The (current) user's language.
   */
  public getLanguage(id?: User['_id']): User['userLanguage'] {
    if (id) {
      return this.get().find(u => u._id === id).userLanguage;
    }
    const user = this.getUserCurrent();
    return user ? user.userLanguage : this.DEFAULT_USER_LANGUAGE;
  }

  /**
   * Get the (current) user's last name.
   *
   * @param [user] When defined, check for this user, otherwise check the current active user.
   * @returns The (current) user's last name.
   */
  public getLastName(user?: User['_id']): User['nameLast'] {
    if (user) {
      return this.get().find(u => u._id === user).nameLast;
    }
    return this.getUserCurrent().nameLast;
  }

  /**
   * Get the (current) user's organization.
   *
   * @param [user] When defined, check for this user, otherwise check the current active user.
   * @returns The (current) user's organization.
   */
  public getOrganization(user?: User['_id']): User['organization'] {
    if (user) {
      return this.get().find(u => u._id === user).organization;
    }
    return this.getUserCurrent().organization;
  }

  /**
   * Get the (current) user's project role.
   *
   * @param [user] When defined, check for this user, otherwise check the current active user.
   * @returns The (current) user's project role.
   */
  public getProjectRole(user?: User['_id']): User['project']['role'] {
    if (user) {
      return this.get().find(u => u._id === user).project.role;
    }
    return this.getUserCurrent().project.role;
  }

  /**
   * Get the (current) user's tenant.
   *
   * @param [user] When defined, check for this user, otherwise check the current active user.
   * @returns The (current) user's tenant.
   */
  public getTenant(user?: User['_id']): User['databases'][0] {
    if (user) {
      return this.get().find(u => u._id === user).databases[0];
    }
    return this.getUserCurrent().databases[0];
  }

  /**
   * Get the user's activation status.
   *
   * @param [user] When defined, check for this user, otherwise check the current active user.
   * @returns Whether the current user is activated.
   */
  public isActivated(user?: User['_id']): User['activated'] {
    if (user) {
      return this.get().find(u => u._id === user).activated;
    }
    return this.getUserCurrent().activated;
  }

  /**
   * Get the user's status.
   *
   * Returns false when the user is soft deleted.
   *
   * @param [user] When defined, check for this user, otherwise check the current active user.
   * @returns Whether the current user is active.
   */
  public isActive(user?: User['_id']): User['active'] {
    if (user) {
      return this.get().find(u => u._id === user).active;
    }
    return this.getUserCurrent().active;
  }

  /**
   * Check whether the user is in the current active project.
   *
   * @param userId User to check.
   * @returns Whether the user is in the current active project.
   */
  public isInProject(userId: User['_id']): boolean {
    return this.projectService.get().users.find(u => u.userId._id === userId) !== undefined;
  }
}
